import React, { useState } from 'react';
import {
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import { ResidentMembersView } from './residents-registry/ResidentMembersView';
import { ResidentVehiclesView } from './residents-registry/ResidentVehiclesView';
import { ResidentKeycardsView } from './residents-registry/ResidentKeycardsView';
import { ResidentForeignOwnershipView } from './residents-registry/ResidentForeignOwnershipView';
import { ResidentPetsView } from './residents-registry/ResidentPetsView';
import { ResidentTitleDeedsView } from './residents-registry/ResidentTitleDeedsView';
import { ResidentHistoryLogsView } from './residents-registry/ResidentHistoryLogsView';

export type ResidentSubMenuKey =
  | 'members'
  | 'vehicles'
  | 'keycards'
  | 'foreign_ownership'
  | 'pets'
  | 'title_deeds'
  | 'history_logs';

export const ResidentDirectory: React.FC = () => {
  const [activeSubMenu, setActiveSubMenu] = useState<ResidentSubMenuKey | null>(null);

  // Sub-view renders
  if (activeSubMenu === 'members') {
    return (
      <ResidentMembersView
        onBack={() => setActiveSubMenu(null)}
        onNavigate={(tab) => setActiveSubMenu(tab)}
      />
    );
  }

  if (activeSubMenu === 'vehicles') {
    return (
      <ResidentVehiclesView
        onBack={() => setActiveSubMenu(null)}
        onNavigate={(tab) => setActiveSubMenu(tab)}
      />
    );
  }

  if (activeSubMenu === 'keycards') {
    return (
      <ResidentKeycardsView
        onBack={() => setActiveSubMenu(null)}
        onNavigate={(tab) => setActiveSubMenu(tab)}
      />
    );
  }

  if (activeSubMenu === 'foreign_ownership') {
    return (
      <ResidentForeignOwnershipView
        onBack={() => setActiveSubMenu(null)}
        onNavigate={(tab) => setActiveSubMenu(tab)}
      />
    );
  }

  if (activeSubMenu === 'pets') {
    return (
      <ResidentPetsView
        onBack={() => setActiveSubMenu(null)}
        onNavigate={(tab) => setActiveSubMenu(tab)}
      />
    );
  }

  if (activeSubMenu === 'title_deeds') {
    return (
      <ResidentTitleDeedsView
        onBack={() => setActiveSubMenu(null)}
        onNavigate={(tab) => setActiveSubMenu(tab)}
      />
    );
  }

  if (activeSubMenu === 'history_logs') {
    return (
      <ResidentHistoryLogsView
        onBack={() => setActiveSubMenu(null)}
        onNavigate={(tab) => setActiveSubMenu(tab)}
      />
    );
  }

  // Exact 2-Column Menu Layout Matching Uploaded Image
  return (
    <div className="space-y-6 font-prompt py-4 sm:py-6">
      {/* Centered Page Title matching image */}
      <div className="text-center mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
          ทะเบียนเจ้าของร่วม/สมาชิก
        </h1>
      </div>

      {/* 2-Column Cards Grid matching image */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 px-2 sm:px-4">
        {/* Left Column (4 cards) */}
        <div className="space-y-4">
          {/* 1. เจ้าของร่วม/สมาชิก */}
          <button
            type="button"
            onClick={() => setActiveSubMenu('members')}
            className="w-full bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal-400 hover:bg-slate-50/50 flex items-center justify-between text-left transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              {/* Teal ID Card Icon */}
              <div className="w-10 h-10 rounded-xl bg-teal-50/80 border border-teal-100 flex items-center justify-center text-teal-600 group-hover:scale-105 transition-transform shrink-0">
                <svg
                  className="w-6 h-6 stroke-teal-600 fill-none stroke-[1.6]"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="4" width="20" height="16" rx="3" />
                  <circle cx="8" cy="11" r="2.5" />
                  <path d="M4.5 17c.8-2 2.2-2.5 3.5-2.5s2.7.5 3.5 2.5" />
                  <line x1="14" y1="9" x2="19" y2="9" />
                  <line x1="14" y1="13" x2="18" y2="13" />
                  <line x1="14" y1="17" x2="17" y2="17" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-teal-900 transition-colors">
                เจ้าของร่วม/สมาชิก
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
          </button>

          {/* 2. ยานพาหนะ */}
          <button
            type="button"
            onClick={() => setActiveSubMenu('vehicles')}
            className="w-full bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal-400 hover:bg-slate-50/50 flex items-center justify-between text-left transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              {/* Teal Car Icon */}
              <div className="w-10 h-10 rounded-xl bg-teal-50/80 border border-teal-100 flex items-center justify-center text-teal-600 group-hover:scale-105 transition-transform shrink-0">
                <svg
                  className="w-6 h-6 stroke-teal-600 fill-none stroke-[1.6]"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.8C2.1 11 2 11.5 2 12v4c0 .6.4 1 1 1h2" />
                  <circle cx="7" cy="17" r="2" />
                  <path d="M9 17h6" />
                  <circle cx="17" cy="17" r="2" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-teal-900 transition-colors">
                ยานพาหนะ
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
          </button>

          {/* 3. คีย์การ์ด */}
          <button
            type="button"
            onClick={() => setActiveSubMenu('keycards')}
            className="w-full bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal-400 hover:bg-slate-50/50 flex items-center justify-between text-left transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              {/* Teal Card with Key/Chip Icon */}
              <div className="w-10 h-10 rounded-xl bg-teal-50/80 border border-teal-100 flex items-center justify-center text-teal-600 group-hover:scale-105 transition-transform shrink-0">
                <svg
                  className="w-6 h-6 stroke-teal-600 fill-none stroke-[1.6]"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="5" width="20" height="14" rx="3" />
                  <circle cx="8" cy="12" r="2" />
                  <path d="M10 12h5" />
                  <path d="M13 10v4" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-teal-900 transition-colors">
                คีย์การ์ด
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
          </button>

          {/* 4. กรรมสิทธิ์ชาวต่างชาติ */}
          <button
            type="button"
            onClick={() => setActiveSubMenu('foreign_ownership')}
            className="w-full bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal-400 hover:bg-slate-50/50 flex items-center justify-between text-left transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              {/* Teal Foreign ID Badge Icon */}
              <div className="w-10 h-10 rounded-xl bg-teal-50/80 border border-teal-100 flex items-center justify-center text-teal-600 group-hover:scale-105 transition-transform shrink-0">
                <svg
                  className="w-6 h-6 stroke-teal-600 fill-none stroke-[1.6]"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="16" rx="3" />
                  <circle cx="8.5" cy="10.5" r="2" />
                  <path d="M6 16c.5-1.5 1.5-2 2.5-2s2 .5 2.5 2" />
                  <path d="M14 9h4" />
                  <path d="M14 13h4" />
                  <path d="M14 17h2" />
                  <circle cx="12" cy="2" r="1" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-teal-900 transition-colors">
                กรรมสิทธิ์ชาวต่างชาติ
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
          </button>
        </div>

        {/* Right Column (3 cards) */}
        <div className="space-y-4">
          {/* 5. สัตว์เลี้ยง */}
          <button
            type="button"
            onClick={() => setActiveSubMenu('pets')}
            className="w-full bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal-400 hover:bg-slate-50/50 flex items-center justify-between text-left transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              {/* Teal Pet Face Icon */}
              <div className="w-10 h-10 rounded-xl bg-teal-50/80 border border-teal-100 flex items-center justify-center text-teal-600 group-hover:scale-105 transition-transform shrink-0">
                <svg
                  className="w-6 h-6 stroke-teal-600 fill-none stroke-[1.6]"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M8 5c-1-2-3-2.5-4-2s-1 3.5 0 5" />
                  <path d="M16 5c1-2 3-2.5 4-2s1 3.5 0 5" />
                  <path d="M4 10c-1 3 0 7 3 9 2 1.5 8 1.5 10 0 3-2 4-6 3-9" />
                  <circle cx="9" cy="11.5" r="0.75" fill="currentColor" />
                  <circle cx="15" cy="11.5" r="0.75" fill="currentColor" />
                  <path d="M11 14.5c.5.5 1.5.5 2 0" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-teal-900 transition-colors">
                สัตว์เลี้ยง
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
          </button>

          {/* 6. กรรมสิทธิ์ */}
          <button
            type="button"
            onClick={() => setActiveSubMenu('title_deeds')}
            className="w-full bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal-400 hover:bg-slate-50/50 flex items-center justify-between text-left transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              {/* Teal Hand holding key / Ownership Icon */}
              <div className="w-10 h-10 rounded-xl bg-teal-50/80 border border-teal-100 flex items-center justify-center text-teal-600 group-hover:scale-105 transition-transform shrink-0">
                <svg
                  className="w-6 h-6 stroke-teal-600 fill-none stroke-[1.6]"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="17" cy="8" r="2.5" />
                  <path d="M14.5 8H8l-1 2-1.5-1-1.5 1" />
                  <path d="M3 14h5l4 3h7c1 0 2-.8 2-1.8 0-.5-.3-1-.7-1.3l-3.3-2.4H8" />
                  <path d="M3 18h4" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-teal-900 transition-colors">
                กรรมสิทธิ์
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
          </button>

          {/* 7. ประวัติผู้อยู่อาศัย */}
          <button
            type="button"
            onClick={() => setActiveSubMenu('history_logs')}
            className="w-full bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal-400 hover:bg-slate-50/50 flex items-center justify-between text-left transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              {/* Teal Resident History Profile Icon */}
              <div className="w-10 h-10 rounded-xl bg-teal-50/80 border border-teal-100 flex items-center justify-center text-teal-600 group-hover:scale-105 transition-transform shrink-0">
                <svg
                  className="w-6 h-6 stroke-teal-600 fill-none stroke-[1.6]"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <circle cx="12" cy="13" r="2" />
                  <path d="M9 18c.5-1.5 1.5-2 3-2s2.5.5 3 2" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-teal-900 transition-colors">
                ประวัติผู้อยู่อาศัย
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
          </button>
        </div>
      </div>
    </div>
  );
};
